<script setup>
/**
 * SzChatListEmpty — empty state da lista de conversas (Brasa).
 *
 * Mostra ícone + título + subtítulo conforme o contexto do filtro ativo:
 *   - mine      → "Nenhuma conversa atribuída a você"   (sem CTA)
 *   - unread    → "Nenhuma mensagem não lida"            (CTA "Mostrar minhas")
 *   - mentions  → "Nenhuma menção encontrada"            (CTA "Mostrar minhas")
 *   - filtered  → "Nenhuma conversa encontrada"          (sem CTA)
 *
 * Background isométrico (painel solar) sutil por trás do conteúdo.
 */
import { computed } from 'vue';

const props = defineProps({
  filter: {
    type: String,
    default: 'mine',
    validator: v => ['mine', 'unread', 'mentions', 'filtered'].includes(v),
  },
});
const emit = defineEmits(['showMine']);

const ICONS = {
  mine:     '/static/img/sz-empty-mine.png',
  unread:   '/static/img/sz-empty-unread.png',
  mentions: '/static/img/sz-empty-mentions.png',
  filtered: '/static/img/sz-empty-mine.png',
};
const COPY = {
  mine: {
    title: 'Nenhuma conversa atribuída a você',
    subtitle: 'Novas conversas aparecerão aqui.',
    cta: null,
  },
  unread: {
    title: 'Nenhuma mensagem não lida',
    subtitle: 'Você já viu tudo.',
    cta: 'Mostrar minhas conversas',
  },
  mentions: {
    title: 'Nenhuma menção encontrada',
    subtitle: 'Quando alguém te marcar, ela aparecerá aqui.',
    cta: 'Mostrar minhas conversas',
  },
  filtered: {
    title: 'Nenhuma conversa encontrada',
    subtitle: 'Não há conversas que correspondam aos filtros selecionados. Ajuste os filtros ou aguarde novas mensagens.',
    cta: null,
  },
};

const iconSrc = computed(() => ICONS[props.filter] || ICONS.mine);
const copy = computed(() => COPY[props.filter] || COPY.mine);

function onCtaClick() {
  emit('showMine');
}
</script>

<template>
  <div class="sz-cle">
    <img :src="iconSrc" alt="" class="sz-cle-icon" />
    <div class="sz-cle-text">
      <div class="sz-cle-title">{{ copy.title }}</div>
      <div class="sz-cle-subtitle">{{ copy.subtitle }}</div>
    </div>
    <button
      v-if="copy.cta"
      type="button"
      class="sz-cle-cta"
      @click="onCtaClick"
    >{{ copy.cta }}</button>
  </div>
</template>

<style scoped>
.sz-cle {
  flex: 1 1 0%;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 64px 24px 24px;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, sans-serif;
  /* BG painel solar isométrico — light por padrão */
  background-image: url('/static/img/sz-empty-bg-light.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
.dark .sz-cle {
  background-image: url('/static/img/sz-empty-bg-dark.png');
}
.sz-cle-icon {
  width: 64px;
  height: 64px;
  object-fit: contain;
  display: block;
}
.sz-cle-text {
  text-align: center;
  max-width: 320px;
}
.sz-cle-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #1F2937;
  margin-bottom: 4px;
}
.sz-cle-subtitle {
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #6B7280;
}
.dark .sz-cle-title { color: #F3F4F6; }
.dark .sz-cle-subtitle { color: #9CA3AF; }

.sz-cle-cta {
  margin-top: 8px;
  background: transparent;
  border: none;
  color: #388E3C;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 0;
}
.sz-cle-cta:hover { color: #2E7D32; text-decoration: underline; }
.dark .sz-cle-cta { color: #7CCF00; }
.dark .sz-cle-cta:hover { color: #94D850; }
</style>
